import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  Icon,
  PageHeader,
  SkeletonBar,
  inkA,
  tokens,
  useIsMobile,
} from 'components/v3';
import FormsSelect from '../../Forms/components/FormsSelect';
import { w9DashboardPath, w9LinkPath, w9SettingsPath } from '../shared/routes';
import { groupAccounts } from '../shared/useW9Accounts';

// The W-9 section's chrome (design w9_app.jsx W9App header): the
// "W-9 Collection" title card with the account picker and the three page
// tabs, plus the notices every page shows before it has a form (pick an
// account, loading, couldn't load) and the per-page heading.

export const PAGES = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'layout',
    path: w9DashboardPath,
  },
  { id: 'link', label: 'Collection Link', icon: 'link', path: w9LinkPath },
  { id: 'settings', label: 'Settings', icon: 'settings', path: w9SettingsPath },
];

export const PICK_TITLE = 'Pick a track or series';
export const PICK_BODY =
  'Every track and series gets one permanent W-9 link. Pick one to open its collection.';

const Tab = ({ to, active, icon, label, fill }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: fill ? 1 : 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        minHeight: fill ? 40 : 34,
        padding: '0 14px',
        borderRadius: 7,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: active ? t.primary : 'transparent',
        color: active ? 'white' : t.sub,
      }}
    >
      <Icon name={icon} size={15} color="currentColor" />
      {label}
    </Link>
  );
};

Tab.propTypes = {
  to: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  fill: PropTypes.bool.isRequired,
};

export const SectionHeader = ({
  page,
  routePrefix,
  accountId,
  accounts,
  accountsLoading,
  onPickAccount,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();
  const options = groupAccounts(accounts);
  // An account the list doesn't know (deactivated, or the list hasn't
  // loaded) still shows as picked rather than blank.
  if (
    accountId &&
    !accounts.some((a) => a.id === accountId) &&
    !accountsLoading
  ) {
    options.push({
      label: 'Other',
      options: [{ label: `Account ${accountId}`, value: String(accountId) }],
    });
  }
  return (
    <PageHeader icon="file-text" title="W-9 Collection">
      <div style={{ width: isMobile ? '100%' : 280, minWidth: 0 }}>
        <FormsSelect
          id="w9-account"
          ariaLabel="Account"
          icon="flag"
          placeholder={accountsLoading ? 'Loading accounts…' : PICK_TITLE}
          value={accountId ? String(accountId) : null}
          options={options}
          disabled={accountsLoading && !accountId}
          onChange={(next) => {
            if (next && Number(next) !== accountId) onPickAccount(Number(next));
          }}
        />
      </div>
      <nav
        aria-label="W-9 pages"
        style={{
          display: 'flex',
          gap: 3,
          padding: 3,
          borderRadius: 10,
          border: '1px solid ' + t.border,
          background: inkA(theme, 0.04),
          width: isMobile ? '100%' : 'auto',
          boxSizing: 'border-box',
        }}
      >
        {PAGES.map((p) => (
          <Tab
            key={p.id}
            to={p.path(routePrefix, { account: accountId })}
            active={page === p.id}
            icon={p.icon}
            label={isMobile && p.id === 'link' ? 'Link' : p.label}
            fill={isMobile}
          />
        ))}
      </nav>
    </PageHeader>
  );
};

SectionHeader.propTypes = {
  page: PropTypes.oneOf(['dashboard', 'link', 'settings']).isRequired,
  routePrefix: PropTypes.string.isRequired,
  accountId: PropTypes.number,
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(['track', 'series']).isRequired,
    })
  ),
  accountsLoading: PropTypes.bool,
  onPickAccount: PropTypes.func.isRequired,
};

SectionHeader.defaultProps = {
  accountId: null,
  accounts: [],
  accountsLoading: false,
};

// The big centred notice: pick an account, or the form couldn't load.
export const SectionNotice = ({ icon, title, body, action, alert }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      role={alert ? 'alert' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '56px 20px',
        textAlign: 'center',
        borderRadius: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        color: t.sub,
      }}
    >
      <Icon name={icon} size={30} color={inkA(theme, 0.3)} />
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: t.text }}>
        {title}
      </h2>
      {body ? (
        <div style={{ maxWidth: 420, fontSize: 14, lineHeight: 1.5 }}>
          {body}
        </div>
      ) : null}
      {action}
    </div>
  );
};

SectionNotice.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  body: PropTypes.node,
  action: PropTypes.node,
  alert: PropTypes.bool,
};

SectionNotice.defaultProps = {
  icon: 'file-text',
  body: null,
  action: null,
  alert: false,
};

// What every page shows until the account's form is ready.
export const SectionBody = ({
  accountId,
  form,
  loading,
  error,
  onRetry,
  children,
}) => {
  if (!accountId) return <SectionNotice title={PICK_TITLE} body={PICK_BODY} />;
  if (error && !form) {
    return (
      <SectionNotice
        alert
        icon="triangle-alert"
        title="Couldn't open this W-9 collection"
        body={error}
        action={
          <ButtonV2 onClick={onRetry} style={{ width: 'auto', marginTop: 6 }}>
            Retry
          </ButtonV2>
        }
      />
    );
  }
  if (loading || !form) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Loading W-9 collection"
        style={{ display: 'grid', gap: 16 }}
      >
        <SkeletonBar $w="100%" $h="72px" $r="14px" />
        <SkeletonBar $w="100%" $h="320px" $r="16px" />
      </div>
    );
  }
  return children(form);
};

SectionBody.propTypes = {
  accountId: PropTypes.number,
  form: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  // (form) → node
  children: PropTypes.func.isRequired,
};

SectionBody.defaultProps = {
  accountId: null,
  form: null,
  loading: false,
  error: null,
};

// "2026 tax year [Collecting]" + the sub line (design W9Parts.PageHead).
export const PageHead = ({ title, badge, sub }) => {
  const t = tokens(useTheme());
  const isMobile = useIsMobile();
  return (
    <div style={{ minWidth: 0 }}>
      <h2
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? 10 : 12,
          margin: 0,
          fontSize: isMobile ? 21 : 24,
          fontWeight: 800,
          letterSpacing: '-0.4px',
          color: t.text,
        }}
      >
        {title}
        {badge}
      </h2>
      {sub ? (
        <div style={{ marginTop: 4, fontSize: 13.5, color: t.sub }}>{sub}</div>
      ) : null}
    </div>
  );
};

PageHead.propTypes = {
  title: PropTypes.string.isRequired,
  badge: PropTypes.node,
  sub: PropTypes.node,
};

PageHead.defaultProps = { badge: null, sub: null };

// A plain card for the Link and Settings pages.
export const Card = ({ title, action, children, style }) => {
  const t = tokens(useTheme());
  return (
    <section
      aria-label={title || undefined}
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        overflow: 'hidden',
        minWidth: 0,
        ...style,
      }}
    >
      {title ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 48,
            padding: '0 10px 0 18px',
            borderBottom: '1px solid ' + t.line,
          }}
        >
          <h3
            style={{
              flex: 1,
              margin: 0,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: t.label,
            }}
          >
            {title}
          </h3>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
};

Card.propTypes = {
  title: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node,
  style: PropTypes.object,
};

Card.defaultProps = { title: null, action: null, children: null, style: {} };
